import * as React from 'react';
import { t } from '@lingui/core/macro';
import Icon from 'components/Icon';
import { MinimizeIcon } from 'components/icons/MinimizeIcon';
import ParticipantVideo from 'components/ParticipantVideo';
import { Video } from 'components/Video';
import { myCameraStream } from 'state/video';
import { userSession } from 'state/session';
import MultiVoip from './MultiVoip';
import type { ActiveVoipParticipant } from './MultiVoip';

// AVS-4275: shared person-avatar placeholder used in BOTH minimized pill tiles when
// the source video is unavailable (camera off / remote videoMuted / awaiting). Mirrors
// what <VideoPlaceholder /> renders in the expanded panel so the two views stay visually
// consistent — was previously a strikethrough camera <VideoIcon off /> for the muted cases.
const PillAvatarPlaceholder: React.FC = () => (
  <div className="ex3d-voip-pill__placeholder">
    <Icon className="ex3d-icon--avatar ex3d-voip-pill__avatar" />
  </div>
);

export interface VoipPanelProps {
  className?: string;
  controls?: boolean;
  isActiveSpeaker?: boolean;
  maxParticipants?: number;
  /** When true, sets `data-call-live="true"` on the pill so CSS can escalate its z-index above modal scrims. */
  voipStream?: boolean;
  /**
   * Connection state forwarded to the pill via `data-connection`. Spec §7 / §5: `"lost"` triggers
   * the red `ex3d-voip-disconnect-pulse` so the user is alerted without expanding the panel.
   */
  connectionState?: 'connected' | 'lost' | 'reconnecting';
  /**
   * Bubbled from MultiVoip so callers (View.tsx) can keep `isActiveSpeaker` in sync without
   * re-mounting `useVoipConnections` (which drives `voipRtcConnection.publish()`).
   */
  onActiveSpeakerChange?: (isActiveSpeaker: boolean) => void;
}

const LIVE_REGION_DEBOUNCE_MS = 300;

const VoipPanel: React.FC<VoipPanelProps> = ({
  className,
  controls,
  isActiveSpeaker,
  voipStream,
  connectionState,
  onActiveSpeakerChange,
  maxParticipants,
}) => {
  const [isMinimized, setIsMinimized] = React.useState(false);
  const [liveRegionMessage, setLiveRegionMessage] = React.useState('');

  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const pillRef = React.useRef<HTMLButtonElement | null>(null);
  const minimizeBtnRef = React.useRef<HTMLButtonElement | null>(null);

  // Track previous connectionState so we only announce on actual transitions to 'lost',
  // not on every render. Color-blind / reduced-motion users rely on this announcement
  // because the red disconnect-pulse animation is suppressed under prefers-reduced-motion.
  const prevConnectionStateRef = React.useRef<typeof connectionState>(connectionState);

  // Track which control should receive focus once the next transform transition ends.
  const pendingFocusRef = React.useRef<'pill' | 'panel' | null>(null);

  // 300ms-debounced live region updates so rapid toggles do not stack announcements.
  const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingMessageRef = React.useRef<string | null>(null);

  const [activeParticipant, setActiveParticipant] = React.useState<ActiveVoipParticipant>(null);

  const handleActiveParticipantChange = React.useCallback((participant: ActiveVoipParticipant) => {
    setActiveParticipant(participant);
  }, []);

  // AVS-4302: a standby dealer's own face arrives as a REMOTE loopback (the master tab's
  // stream), lifted from MultiVoipInternal. The collapsed pill renders it in the "You" tile
  // so a minimized standby still sees itself — a standby has no `myCameraStream` to fall back on.
  const [selfMirror, setSelfMirror] = React.useState<ActiveVoipParticipant>(null);

  const handleSelfMirrorChange = React.useCallback((mirror: ActiveVoipParticipant) => {
    setSelfMirror(mirror);
  }, []);

  // Pill fallback chain: remote video → dealer's own camera → static icon.
  // Subscribing to `myCameraStream().useState()` keeps the pill reactive when the dealer
  // toggles their camera on/off; the manager's `.stream` reference is read directly
  // because it lives on the manager instance, not the reactive state object.
  const cameraStream = myCameraStream().useState();
  const ownCameraStream = myCameraStream().stream;
  // Subscribe to the session state machine so the "Them" tile label updates reactively
  // when the dealer name resolves (same pattern as MultiVoipInternal).
  const session = userSession().useState();
  const dealerName = session.dealerName;
  const hasRemoteVideo = !!activeParticipant && !activeParticipant.videoMuted;
  const hasRemoteParticipant = !!activeParticipant;
  const showOwnVideo =
    !!cameraStream.mediaAllowed && !!cameraStream.isVideoEnabled && ownCameraStream instanceof MediaStream;

  React.useEffect(
    () => () => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }
    },
    []
  );

  const queueAnnouncement = React.useCallback((message: string) => {
    pendingMessageRef.current = message;
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      if (pendingMessageRef.current !== null) {
        setLiveRegionMessage(pendingMessageRef.current);
        pendingMessageRef.current = null;
      }
      debounceTimerRef.current = null;
    }, LIVE_REGION_DEBOUNCE_MS);
  }, []);

  // Announce connection drop via aria-live (WCAG fallback for users who can't see
  // the red disconnect-pulse — reduced-motion suppresses the animation, and
  // color-blind users may not register the red ring). Fires only on transition.
  React.useEffect(() => {
    if (prevConnectionStateRef.current !== 'lost' && connectionState === 'lost') {
      queueAnnouncement(t`Connection lost`);
    }
    prevConnectionStateRef.current = connectionState;
  }, [connectionState, queueAnnouncement]);

  const handleMinimize = React.useCallback(
    (event?: React.SyntheticEvent<HTMLButtonElement>) => {
      // AVS-4218 hotfix: the minimize button overlaps the EndSessionButton in the
      // controls bar at the panel's top-right. Stop propagation so the click cannot
      // reach any ancestor handler that might end the session, and prevent default
      // so a focused button doesn't double-trigger via implicit form/submit semantics.
      event?.stopPropagation();
      event?.preventDefault();
      pendingFocusRef.current = 'pill';
      setIsMinimized(true);
      queueAnnouncement(t`Call window minimized`);
    },
    [queueAnnouncement]
  );

  const handleExpand = React.useCallback(
    (event?: React.SyntheticEvent<HTMLButtonElement>) => {
      // Mirror the minimize handler: keep the expand click self-contained so it cannot
      // bubble to ancestors that listen for clicks on the panel region.
      event?.stopPropagation();
      event?.preventDefault();
      pendingFocusRef.current = 'panel';
      setIsMinimized(false);
      queueAnnouncement(t`Call window expanded`);
    },
    [queueAnnouncement]
  );

  const handleMinimizeKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        handleMinimize();
      }
    },
    [handleMinimize]
  );

  const handlePanelTransitionEnd = React.useCallback((event: React.TransitionEvent<HTMLDivElement>) => {
    // Ignore bubbled transitions from descendants (e.g. participant tile animations).
    if (event.target !== event.currentTarget) return;
    if (event.propertyName !== 'transform') return;
    const target = event.currentTarget;
    // Clear will-change so the panel does not retain a permanent GPU layer between toggles.
    target.style.willChange = 'auto';
    if (pendingFocusRef.current === 'pill' && pillRef.current) {
      pillRef.current.focus();
      pendingFocusRef.current = null;
    } else if (pendingFocusRef.current === 'panel' && minimizeBtnRef.current) {
      minimizeBtnRef.current.focus();
      pendingFocusRef.current = null;
    }
  }, []);

  const handlePillTransitionEnd = React.useCallback((event: React.TransitionEvent<HTMLButtonElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.propertyName !== 'transform') return;
    event.currentTarget.style.willChange = 'auto';
  }, []);

  const minimizeLabel = t`Minimize call window`;
  const expandLabel = t`Expand call window`;

  const panelClassName = className ?? '';

  return (
    <>
      <div role="status" aria-live="polite" className="sr-only">
        {liveRegionMessage}
      </div>
      <div
        id="voip-panel"
        part="voip-panel"
        ref={panelRef}
        className={panelClassName}
        role="region"
        aria-label={t`Video call`}
        data-state={isMinimized ? 'minimized' : 'expanded'}
        style={{ willChange: 'transform, opacity' }}
        onTransitionEnd={handlePanelTransitionEnd}
      >
        <MultiVoip
          controls={controls}
          maxParticipants={maxParticipants}
          onActiveSpeakerChange={onActiveSpeakerChange}
          onActiveParticipantChange={handleActiveParticipantChange}
          onSelfMirrorChange={handleSelfMirrorChange}
          extraControls={
            !isMinimized ? (
              <button
                type="button"
                ref={minimizeBtnRef}
                className="ex3d-button--minimize"
                aria-label={minimizeLabel}
                aria-expanded={true}
                aria-controls="voip-panel"
                onClick={handleMinimize}
                onKeyDown={handleMinimizeKeyDown}
              >
                <MinimizeIcon />
              </button>
            ) : null
          }
        />
      </div>
      {isMinimized && (
        <button
          type="button"
          ref={pillRef}
          className="ex3d-voip-pill"
          data-state="visible"
          data-active-speaker={isActiveSpeaker ? 'true' : 'false'}
          data-call-live={voipStream ? 'true' : 'false'}
          data-connection={connectionState ?? 'connected'}
          aria-label={expandLabel}
          aria-expanded={false}
          aria-controls="voip-panel"
          style={{ willChange: 'transform, opacity' }}
          onClick={handleExpand}
          onTransitionEnd={handlePillTransitionEnd}
        >
          {/* "You" tile (left) — own camera, or a standby's master-loopback mirror.
              `videoMuted={false}` so ParticipantVideo renders our children verbatim
              rather than swapping in <VideoPlaceholder />. */}
          <ParticipantVideo
            audioMuted={selfMirror ? selfMirror.audioMuted : !cameraStream.isAudioEnabled}
            videoMuted={false}
            className="ex3d-voip-pill__tile ex3d-voip-pill__tile--you"
            aria-hidden="true"
          >
            {selfMirror ? (
              // AVS-4302: standby — mirror the master tab's loopback (the dealer's own face) so
              // the minimized pill still shows "you" from the other tab. Force audioMuted (it is
              // the dealer's own audio — playing it back echoes). Avatar when the master's video
              // is off, matching the remote-tile videoMuted behavior.
              !selfMirror.videoMuted ? (
                <Video stream={selfMirror.stream} audioMuted videoClassName="size-full object-cover" />
              ) : (
                <PillAvatarPlaceholder />
              )
            ) : showOwnVideo && ownCameraStream ? (
              /* Video auto-mutes own stream via its `isMyOwnVideo` check, so no echo. */
              <Video stream={ownCameraStream} videoClassName="size-full object-cover" />
            ) : (
              // AVS-4275: camera-off uses the same person-avatar as the expanded panel so the
              // minimized pill reads identically to the expanded view (no strikethrough camera icon).
              <PillAvatarPlaceholder />
            )}
          </ParticipantVideo>
          {/* "Them" tile (right) — only renders when a remote participant is present.
              AVS-4275: when alone in the call, the second tile is dropped entirely so the
              pill collapses to a single You tile. */}
          {hasRemoteParticipant && (
            <ParticipantVideo
              audioMuted={!!activeParticipant?.audioMuted}
              videoMuted={false}
              className="ex3d-voip-pill__tile ex3d-voip-pill__tile--them"
              aria-hidden="true"
            >
              {dealerName ? <span className="ex3d-voip-tile-label">{dealerName}</span> : null}
              {hasRemoteVideo && activeParticipant ? (
                <Video stream={activeParticipant.stream} audioMuted={true} videoClassName="size-full object-cover" />
              ) : (
                // AVS-4275: remote with camera off uses the same person-avatar placeholder
                // the expanded panel uses (was a strikethrough camera icon — wrong icon per PO).
                <PillAvatarPlaceholder />
              )}
            </ParticipantVideo>
          )}
        </button>
      )}
    </>
  );
};

export default VoipPanel;
